import { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import { useParams, useNavigate } from 'react-router-dom';
import useDeviceKeys from './hooks/useDeviceKeys';
import useUserOrg from '../../Authentication/components/hooks/useUserOrg';
import useAuthenticatedApi from '../../Authentication/components/hooks/useAuthenticatedApi';
import { Button } from '@/src/shadComponents/ui/button';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/src/shadComponents/ui/select';
import {
    CardContent,
    CardHeader,
    CardTitle,
} from '@/src/shadComponents/ui/card';
import {
    FormControl,
    FormDescription,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '@/src/shadComponents/ui/form';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Form } from '@/src/shadComponents/ui/form';
import { toast } from '@/src/shadComponents/ui/use-toast';

const formSchema = z.object({
    keyId: z.string().min(1, { message: 'Please select a device key' }),
});

export default function AssignDeviceKey() {
    const { groupId } = useParams();
    const { orgId } = useUserOrg();
    const navigate = useNavigate();
    const authenticatedApi = useAuthenticatedApi();
    const [isLoading, setIsLoading] = useState(true);
    const [assignableKeys, setAssignableKeys] = useState<DeviceKey[]>([]);
    const [group, setGroup] = useState<DeviceGroup | null>(null);
    const { getDeviceKeys, getDeviceKeysForGroup } = useDeviceKeys(orgId);

    const form = useForm<z.infer<typeof formSchema>>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            keyId: '',
        },
    });

    // Load available device keys and group details
    useEffect(() => {
        let isMounted = true;

        const loadData = async () => {
            try {
                // Get all available device keys
                const keys = await getDeviceKeys();

                // Get current group info and its keys
                const groupData = await getDeviceKeysForGroup(+(groupId || 0));

                if (isMounted) {
                    // Filter out keys already in the group
                    const groupKeyIds = groupData.keys.map(
                        (key: DeviceKey) => key.id,
                    );
                    const availableKeys = keys.filter(
                        (key: DeviceKey) => !groupKeyIds.includes(key.id),
                    );

                    setAssignableKeys(availableKeys);
                    setGroup(groupData.group);
                    setIsLoading(false);
                }
            } catch (error) {
                console.error('Error loading data:', error);
                if (isMounted) {
                    setIsLoading(false);
                }
            }
        };

        loadData();

        return () => {
            isMounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onSubmit = async (data: z.infer<typeof formSchema>) => {
        try {
            setIsLoading(true);

            const response = await authenticatedApi.post(
                `${orgId}/device-groups/${groupId}/${data.keyId}`,
                JSON.stringify({ action: 'assign' }),
            );

            if (response.status === 200) {
                toast({
                    title: 'Success',
                    description: 'Device key successfully assigned to group',
                });
                navigate(
                    `/device-management/device-groups/${groupId}/device-keys`,
                );
            }
        } catch (error) {
            console.error('Error assigning device key:', error);
            toast({
                title: 'Error',
                description: 'Failed to assign device key',
                variant: 'destructive',
            });
        } finally {
            setIsLoading(false);
        }
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-12">
                <Loader2 className="size-6 animate-spin text-muted-foreground" />
            </div>
        );
    }

    return (
        <div className="mx-8 my-4">
            <CardHeader>
                <CardTitle>Assign Device Key to {group?.name}</CardTitle>
            </CardHeader>
            <CardContent>
                <Form {...form}>
                    <form
                        onSubmit={form.handleSubmit(onSubmit)}
                        className="space-y-6"
                    >
                        <FormField
                            control={form.control}
                            name="keyId"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Select Device Key</FormLabel>
                                    <Select
                                        onValueChange={field.onChange}
                                        defaultValue={field.value}
                                    >
                                        <FormControl>
                                            <SelectTrigger>
                                                <SelectValue placeholder="Select a device key" />
                                            </SelectTrigger>
                                        </FormControl>
                                        <SelectContent>
                                            {assignableKeys.length === 0 ? (
                                                <SelectItem
                                                    value="none"
                                                    disabled
                                                >
                                                    No available device keys
                                                </SelectItem>
                                            ) : (
                                                assignableKeys.map((key) => (
                                                    <SelectItem
                                                        key={key.id}
                                                        value={key.id.toString()}
                                                    >
                                                        {key.name} ({key.keyId})
                                                    </SelectItem>
                                                ))
                                            )}
                                        </SelectContent>
                                    </Select>
                                    <FormDescription>
                                        Select a device key to assign to this
                                        group
                                    </FormDescription>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <div className="flex justify-end space-x-4">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() =>
                                    navigate(
                                        `/device-management/device-groups/${groupId}/device-keys`,
                                    )
                                }
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                disabled={assignableKeys.length === 0}
                            >
                                Assign Device Key
                            </Button>
                        </div>
                    </form>
                </Form>
            </CardContent>
        </div>
    );
}
